import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  Tab,
} from "@mui/material";
import {
  GridColDef,
  GridRenderCellParams,
  GridValueGetterParams,
} from "@mui/x-data-grid";
import { useDispatch } from "react-redux";
import {
  addBulkPartMatchedProducts,
  addMatchedProduct,
  removeAllMissMatchPart,
  removeBulkMissMatchPart,
  removeMissMatchProduct,
} from "../../../redux/reducer/features/Integration/DmsSetupSlice";
import * as React from "react";
import { useSelector } from "react-redux";
import AddCircleIcon from "@mui/icons-material/AddCircleOutline";
import { AppState } from "../../../redux/reducer/rootReducer";
import AppGrid from "../../app/AppGrid";
import {
  LocationPart,
  LocationWholegood,
} from "../../../models/features/Integration/DmsSetup";
import MatchManually from "./Popup/MatchManually";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { GetAppGridColumns } from "../../../services/utilities/utility";
import { AppGridId } from "../../../models/app/AppGridModels";
import AppConfirmDialog from "../../app/AppConfirmDialog";

interface MismatchProductsProps {
  handleNext: any;
  handleBack: any;
}
const MismatchProducts: React.FunctionComponent<MismatchProductsProps> = ({
  handleBack,
  handleNext,
}) => {
  const gridSetting = useSelector((state: AppState) =>
    state?.AppGridReducer?.setting.find(
      (x) => x.gridId === AppGridId.Create_Setup_Not_Matched_Products
    )
  );
  const [selectedRowData, setSelectedRowData] =
    React.useState<LocationWholegood[]>();
  const [selectedRows, setSelectedRows] = React.useState<any>([]);
  const [selectedPartRows, setSelectedPartRows] = React.useState<any>([]);
  const dispatch = useDispatch();
  const [popup, setPopup] = React.useState(false);
  const [navigateOpen, setNavigateOpen] = React.useState(false);

  const { missMatchedProducts, missMatchedParts } = useSelector(
    (state: AppState) => state.dmsReducer
  );

  React.useEffect(() => {
    const tempSelectedRows: LocationWholegood[] = [];
    selectedRows.forEach((value: any) => {
      tempSelectedRows.push(missMatchedProducts[value]);
    });
    setSelectedRowData(tempSelectedRows);
  }, [selectedRows]);

  const handleClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    item: LocationWholegood
  ) => {
    setSelectedRowData([item]);
  };

  const AddToShopifyInitialization = () => {
    selectedRowData?.forEach((element) => {
      dispatch(
        addMatchedProduct({
          dmsProduct: element as LocationWholegood,
          isManuallyMatched: true,
        })
      );
      dispatch(removeMissMatchProduct(element as LocationWholegood));
    });

    setSelectedRows([]);
    setSelectedRowData([]);
    setPopup(false);
  };

  const handlePopupClose = () => {
    setSelectedRows([]);
    setSelectedRowData([]);
    setPopup(false);
  };

  const columns: GridColDef[] = [
    {
      field: "mfgName",
      headerName: "Manufacturer",
      flex: 1,
      valueGetter: (params: GridValueGetterParams<LocationWholegood>) =>
        params.row.mfrCode + " - " + params.row.mfgName,
    },
    {
      field: "model",
      headerName: "Model",
      flex: 1,
    },
    {
      field: "description",
      headerName: "Description",
      flex: 1,
      valueGetter: (params: GridValueGetterParams<LocationWholegood>) =>
        params.row.description,
      editable: true,
    },
    {
      field: "productLine",
      headerName: "Category",
      flex: 1,
      valueGetter: (params: GridValueGetterParams<LocationWholegood>) =>
        params.row.productLine,
      editable: true,
    },
    {
      field: "subProduct",
      headerName: "Sub-Category",
      flex: 1,
      valueGetter: (params: GridValueGetterParams<LocationWholegood>) =>
        params.row.subProduct,
      editable: true,
    },
    {
      field: "price",
      headerName: "Price ($)",
      headerAlign: "right",
      flex: 1,
      type: "number",
      align: "right",
      valueGetter: (params: GridValueGetterParams<any>) =>
        params.row.price.toFixed(2),
    },
    {
      field: "cost",
      headerName: "Cost ($)",
      headerAlign: "right",
      type: "number",
      flex: 1,
      align: "right",
      filterable: false,
      valueGetter: (params: GridValueGetterParams<any>) =>
        params.row.cost.toFixed(2),
    },
    {
      field: "count",
      headerName: "In Store",
      headerAlign: "right",
      align: "right",
      type: "number",
      flex: 1,
      filterable: false,
      valueGetter: (params: GridValueGetterParams<LocationWholegood>) => {
        if (params.row?.locationCount) {
          return params.row?.locationCount?.reduce(
            (partial: number, value: any) => partial + value.count,
            0
          );
        } else {
          return 0;
        }
      },
    },
    {
      field: "actions",
      headerName: "Match",
      type: "actions",
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        return (
          <div>
            <IconButton
              size="small"
              onClick={(e) => {
                handleClick(e, params.row);
                setPopup(true);
              }}
            >
              <AddCircleIcon />
            </IconButton>
          </div>
        );
      },
    },
  ];

  const partColumns: GridColDef[] = [
    {
      field: "mfrName",
      headerName: "Manufacturer",
      flex: 1,
      valueGetter: (params: GridValueGetterParams<any>) =>
        params.row.mfrCode + " - " + params.row.mfrName,
    },
    {
      field: "inventoryPartId",
      headerName: "Part Id",
      flex: 1,
    },
    {
      field: "partNumber",
      headerName: "Part Number",
      flex: 1,
    },
    {
      field: "description",
      headerName: "Description",
      flex: 1,
    },
    {
      field: "price",
      headerName: "Price ($)",
      headerAlign: "right",
      flex: 1,
      type: "number",
      align: "right",
      valueGetter: (params: GridValueGetterParams<any>) =>
        params.row.price.toFixed(2),
    },
    {
      field: "averageCost",
      headerName: "Cost ($)",
      headerAlign: "right",
      flex: 1,
      align: "right",
      type: "number",
      filterable: false,
      valueGetter: (params: GridValueGetterParams<any>) =>
        params.row.averageCost.toFixed(2),
    },
    {
      field: "availableQty",
      headerName: "Available Qty",
      flex: 1,
      filterable: false,
      type: "number",
    },
  ];
  return (
    <>
      <Box height={620}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
            mt: 2,
            mb: 2,
          }}
        >
          <Button
            disabled={selectedRows.length === 0 ? true : false}
            color="warning"
            variant="contained"
            size="small"
            onClick={() => {
              AddToShopifyInitialization();
            }}
          >
            Sync to Shopify
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              if (selectedRows.length === 0 && selectedPartRows.length === 0) {
                handleBack();
              } else {
                setNavigateOpen(true);
              }
            }}
          >
            Back
          </Button>
          <Button size="small" variant="contained" onClick={handleNext}>
            Next
          </Button>
        </Box>
        <AppGrid
          gridId={AppGridId.Create_Setup_Not_Matched_Products}
          data={missMatchedProducts.map((item, index) => ({
            id: index,
            ...item,
          }))}
          columns={GetAppGridColumns(
            columns,
            gridSetting?.columnVisibilityModel
          )}
          isLoading={false}
          selectedRows={selectedRows}
          onSelect={(newSelection: any) => {
            setSelectedRows(newSelection);
          }}
          height={598}
        ></AppGrid>
      </Box>
      <MatchManually
        isOpen={popup}
        setPopup={setPopup}
        handleClose={handlePopupClose}
        selectedDmsItems={selectedRowData}
      ></MatchManually>
      <AppConfirmDialog
        headerText="Confirm Navigation"
        bodyText="Are you sure you want to go back? You may lose some data."
        onFail={() => {
          setNavigateOpen(false);
        }}
        onSuccess={() => {
          setNavigateOpen(false);
          handleBack();
        }}
        handleClose={() => {
          setNavigateOpen(false);
        }}
        isOpen={navigateOpen}
      />
    </>
  );
};

export default MismatchProducts;
